import { Component, Input, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { PlatformModule } from '@app/platform.module';
import { FlinksIframeView } from '@company/views/overview/flinks-iframe/flinks-iframe.view';
import { BankAccount } from '@models/company/bank-account.model';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent, CardComponent } from '@app/components/platform';
import { FlinksConnect } from '@models/company/flinks-connect.model';
import { differenceInMinutes } from 'date-fns';

type BankAccountStatus = 'loading' | 'unverified' | 'inProgress' | 'verified' | 'failed' | 'syncEnabled';

@Component({
    standalone: true,
    selector: 'app-flinks-bank-account',
    templateUrl: './flinks-bank-account.template.html',
    styleUrls: ['./flinks-bank-account.style.scss'],
    imports: [TranslateModule, ButtonComponent, CardComponent, PlatformModule, FlinksIframeView],
})
export class FlinksBankAccountView implements AfterViewInit, OnChanges {
    @Input() companyId!: number;
    @Input() bankAccount!: BankAccount;
    @Input() isPayrollSyncEnabled: boolean = false;

    bankAccountStatus: BankAccountStatus = 'loading';
    isIframeOpen = false;
    isLoading = false;

    async ngAfterViewInit(): Promise<void> {
        await this.loadBankAccountStatus();
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['bankAccount'] || changes['companyId']) {
            if (this.bankAccountStatus === 'failed' || this.bankAccountStatus === 'inProgress') {
                await this.loadBankAccountStatus();
            }
        }
    }

    onOpenIframe(): void {
        this.isIframeOpen = true;
    }

    onCloseIframe(): void {
        this.isIframeOpen = false;
    }

    onAccountConnectionTriggered(): void {
        this.bankAccountStatus = 'inProgress';
    }

    private async loadBankAccountStatus(): Promise<void> {
        try {
            const [flinksConnectArray] = await FlinksConnect.param('company', this.companyId).get();
            if (!flinksConnectArray.length) {
                this.bankAccountStatus = this.isPayrollSyncEnabled ? 'syncEnabled' : 'unverified';
                return;
            }
            // There is only 1 entry per company
            const flinksConnect = flinksConnectArray[0];

            if (this.bankAccount.id && flinksConnect.bankAccountId === this.bankAccount.id) {
                this.bankAccountStatus = 'verified';
                return;
            }
            if (flinksConnect.updatedAt || flinksConnect.createdAt) {
                const updatedAt = new Date(flinksConnect.updatedAt ?? flinksConnect.createdAt);
                if (Math.abs(differenceInMinutes(new Date(), updatedAt)) > 30) {
                    this.bankAccountStatus = 'failed';
                    return;
                }
            }
            if (flinksConnect.id) {
                this.bankAccountStatus = 'inProgress';
                return;
            }
        } catch (err) {
            // Do nothing
        }

        this.bankAccountStatus = this.isPayrollSyncEnabled ? 'syncEnabled' : 'unverified';
    }
}
